import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelection, type QuerySnapshot } from "./SelectionContext";
import { TagModal } from "./TagModal";
import { AddToPlaylistModal } from "./AddToPlaylistModal";
import { api } from "../api/client";
import { dropIdsFromMediaListCaches } from "../util/media-cache";

interface SelectionToolbarProps {
  isAdmin: boolean;
  /** Current filter snapshot from the grid. Used to gate + dispatch by-query actions. */
  query?: QuerySnapshot;
  /** Server-reported match count for the current filter, if known. */
  matched?: number;
  /** True when any filter is active (search, duration, tag chip, or `tag` route). */
  hasFilter: boolean;
}

export function SelectionToolbar({ isAdmin, query, matched, hasFilter }: SelectionToolbarProps) {
  const sel = useSelection();
  const qc = useQueryClient();
  const [tagOpen, setTagOpen] = useState(false);
  const [plOpen, setPlOpen] = useState(false);

  const ids = sel.getIdsForMutation() ?? [];
  const inQueryMode = sel.isQueryMode;
  const querySnapshot = sel.mode.kind === "query" ? sel.mode.snapshot : null;
  const queryMatched = sel.mode.kind === "query" ? sel.mode.matched : 0;

  const favorite = useMutation({
    mutationFn: ({ on }: { on: boolean }) => {
      if (querySnapshot) {
        return api.bulkFavoriteByQuery({
          kind: querySnapshot.kind,
          tag: querySnapshot.tag,
          structured: querySnapshot.structured,
          on,
        });
      }
      return api.bulkFavorite(ids, on);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media"] });
      qc.invalidateQueries({ queryKey: ["favorites"] });
      sel.clear();
    },
  });

  // Capture the ids we're about to delete before mutationFn runs — `sel` clears
  // after success, and we need the list for the optimistic cache update.
  const idsToDelete = inQueryMode ? [] : ids.slice();

  const del = useMutation<unknown>({
    mutationFn: () => {
      if (querySnapshot) {
        return api.bulkDeleteByQuery({
          kind: querySnapshot.kind,
          tag: querySnapshot.tag,
          structured: querySnapshot.structured,
        });
      }
      return api.bulkDelete(ids);
    },
    onSuccess: (data) => {
      // Ids-mode: deletion is synchronous on the server, so drop the rows
      // from the grid cache immediately. Query-mode is async (a queued job
      // processed by the worker), so the grid stays as-is and updates as
      // the worker chips through — invalidate covers count refresh.
      if (!inQueryMode && idsToDelete.length > 0) {
        dropIdsFromMediaListCaches(qc, idsToDelete);
      }
      qc.invalidateQueries({ queryKey: ["media"] });
      qc.invalidateQueries({ queryKey: ["favorites"] });
      qc.invalidateQueries({ queryKey: ["playlists"] });
      sel.setActive(false);
      if (inQueryMode && data && typeof data === "object" && "queued" in data) {
        const n = (data as { queued: number }).queued;
        window.alert(
          `Queued ${n.toLocaleString()} item${n === 1 ? "" : "s"} for deletion. They'll disappear over the next few minutes.`,
        );
      }
    },
  });

  if (!sel.active) return null;

  // Can offer "select all matching"? Only in ids mode, with an active filter,
  // a known matched count, and matched strictly greater than the visible page.
  const offerSelectAllMatching =
    !inQueryMode &&
    hasFilter &&
    query &&
    matched != null &&
    matched > sel.visibleIds.length;

  const count = sel.count;
  const actionDisabled = count === 0;

  return (
    <>
      <div className="sticky top-0 z-30 flex flex-wrap items-center justify-between gap-2 border-b border-zinc-700 bg-zinc-900/95 px-4 py-2 text-sm shadow backdrop-blur">
        <div className="flex items-center gap-3">
          {inQueryMode ? (
            <span className="text-zinc-300">
              All <b>{queryMatched.toLocaleString()}</b> items matching your search are selected
              <button
                onClick={() => sel.clear()}
                className="ml-2 text-xs text-zinc-400 underline hover:text-zinc-200"
              >
                Clear selection
              </button>
            </span>
          ) : (
            <span className="text-zinc-300">
              {count} selected
              <button
                onClick={() => sel.selectAll()}
                className="ml-2 text-xs text-sky-400 underline hover:text-sky-300"
              >
                Select all visible
              </button>
              {offerSelectAllMatching && (
                <button
                  onClick={() => sel.selectAllMatching(query!, matched!)}
                  className="ml-2 text-xs text-sky-400 underline hover:text-sky-300"
                >
                  … or select all {matched!.toLocaleString()} matching
                </button>
              )}
              {count > 0 && (
                <button
                  onClick={() => sel.clear()}
                  className="ml-2 text-xs text-zinc-400 underline hover:text-zinc-200"
                >
                  Clear
                </button>
              )}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => favorite.mutate({ on: true })}
            disabled={actionDisabled || favorite.isPending}
            className="rounded bg-rose-700/40 px-3 py-1 text-rose-100 hover:bg-rose-700/60 disabled:opacity-50"
          >
            ♥ Favorite
          </button>
          <button
            onClick={() => favorite.mutate({ on: false })}
            disabled={actionDisabled || favorite.isPending}
            className="rounded bg-zinc-700 px-3 py-1 hover:bg-zinc-600 disabled:opacity-50"
          >
            ♡ Unfavorite
          </button>
          <button
            onClick={() => setTagOpen(true)}
            disabled={actionDisabled}
            className="rounded bg-zinc-700 px-3 py-1 hover:bg-zinc-600 disabled:opacity-50"
          >
            🏷 Tag
          </button>
          <button
            onClick={() => setPlOpen(true)}
            disabled={actionDisabled}
            className="rounded bg-zinc-700 px-3 py-1 hover:bg-zinc-600 disabled:opacity-50"
          >
            📂 Add to playlist
          </button>
          {isAdmin && (
            <button
              onClick={() => {
                const what = inQueryMode
                  ? `${queryMatched.toLocaleString()} matching item${queryMatched === 1 ? "" : "s"}`
                  : `${count} item${count === 1 ? "" : "s"}`;
                if (
                  window.confirm(
                    inQueryMode
                      ? `Queue deletion of ${what}?\n\nFiles move to /media/.media-viewer/trash/ over the next few minutes.`
                      : `Delete ${what}? They'll be moved to /media/.media-viewer/trash/.`,
                  )
                ) {
                  del.mutate();
                }
              }}
              disabled={actionDisabled || del.isPending}
              className="rounded bg-rose-800/60 px-3 py-1 text-rose-100 hover:bg-rose-800 disabled:opacity-50"
            >
              🗑 Delete
            </button>
          )}
          <button
            onClick={() => sel.setActive(false)}
            className="rounded bg-zinc-800 px-3 py-1 text-zinc-300 hover:bg-zinc-700"
          >
            ✕ Cancel
          </button>
        </div>
      </div>

      <TagModal
        open={tagOpen}
        onClose={() => setTagOpen(false)}
        mediaIds={ids}
        query={querySnapshot ?? undefined}
        matched={inQueryMode ? queryMatched : undefined}
      />
      <AddToPlaylistModal
        open={plOpen}
        onClose={() => setPlOpen(false)}
        mediaIds={ids}
        query={querySnapshot ?? undefined}
        matched={inQueryMode ? queryMatched : undefined}
      />
    </>
  );
}

/**
 * Small button that lives in a page header. Toggles selection mode.
 */
export function SelectModeToggle() {
  const sel = useSelection();
  return (
    <button
      onClick={() => sel.setActive(!sel.active)}
      className={
        "rounded px-3 py-1 text-xs " +
        (sel.active ? "bg-emerald-700 text-emerald-100" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700")
      }
      title="Toggle multi-select mode"
    >
      {sel.active ? "Selecting…" : "Select"}
    </button>
  );
}
